function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { attachSubComponents, InternalProvider, pxToRem } from '@soluto-private/mx-asurion-ui-react';
import { CarouselContainer, CarouselWrapper, Footer } from './elements';
import { CarouselContext } from './CarouselContext';
import { Controls } from './Controls';
import { CarouselSlide } from './CarouselSlide';
import { SlideIndicator } from './SlideIndicator';
import { ScrollIndicator } from './ScrollIndicator';
export const Carousel = attachSubComponents('Carousel', ({
  as,
  children,
  maxWidth: maxWidthPx = 200,
  variant = 'element',
  showControls = 'onHover',
  ...props
}) => {
  const targetRef = useRef();
  const [pageWidthPx, setPageWidthPx] = useState(0);
  const [carouselWidthPx, setCarouselWidthPx] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);
  useEffect(() => {
    var _targetRef$current, _targetRef$current$sc;
    if (!isInitialRender || !targetRef.current) return;
    setIsInitialRender(false);
    // @ts-ignore
    (_targetRef$current = targetRef.current) === null || _targetRef$current === void 0 ? void 0 : (_targetRef$current$sc = _targetRef$current.scrollTo) === null || _targetRef$current$sc === void 0 ? void 0 : _targetRef$current$sc.call(_targetRef$current, {
      left: 0,
      behavior: 'instant'
    });
  });
  useLayoutEffect(() => {
    const onResize = () => {
      var _window, _targetRef$current2;
      setPageWidthPx(((_window = window) === null || _window === void 0 ? void 0 : _window.innerWidth) ?? 0);
      setCarouselWidthPx((targetRef === null || targetRef === void 0 ? void 0 : (_targetRef$current2 = targetRef.current) === null || _targetRef$current2 === void 0 ? void 0 : _targetRef$current2.offsetWidth) ?? 0);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  });
  const calcCarouselLayout = () => {
    const maxW = pxToRem(maxWidthPx);
    const carouselWidth = pxToRem(carouselWidthPx);
    const numItems = React.Children.count(children);

    // see max number of items that can fit
    const numColumns = Math.floor((carouselWidth + 1) / (maxW + 1)) + 1;
    let columnWidth;
    if (numColumns - 1 >= numItems) {
      columnWidth = maxW + 'rem';
    } else {
      const remainder = (maxW + 1) * numColumns - (carouselWidth + 1);
      columnWidth = maxW - remainder / numColumns + 'rem';
    }
    return {
      numItems,
      numColumns,
      columnWidth
    };
  };
  let indicator = null;
  let elementWidth;
  let bleedElements = 0;
  if (variant === 'page') {
    indicator = /*#__PURE__*/React.createElement(SlideIndicator, null);
    elementWidth = '100%';
  } else if (variant === 'element') {
    indicator = /*#__PURE__*/React.createElement(ScrollIndicator, null);
    elementWidth = calcCarouselLayout().columnWidth;
  } else if (variant === 'slide') {
    indicator = /*#__PURE__*/React.createElement(SlideIndicator, null);
    const {
      numItems,
      numColumns,
      columnWidth
    } = calcCarouselLayout();
    const numSlides = Math.ceil(numItems / numColumns);
    const childArr = React.Children.toArray(children);
    children = Array.from({
      length: numSlides
    }, (_, i) => /*#__PURE__*/React.createElement(CarouselSlide, {
      key: i
    }, childArr.slice(i * numColumns, (i + 1) * numColumns)));
    elementWidth = columnWidth;
  } else if (variant === 'overflow') {
    indicator = /*#__PURE__*/React.createElement(ScrollIndicator, null);
    elementWidth = pxToRem(maxWidthPx) + 'rem';
    bleedElements = Math.ceil(pageWidthPx / 2 / maxWidthPx);
    children = [Array.from({
      length: bleedElements
    }, (_, i) => /*#__PURE__*/React.createElement("li", {
      "aria-hidden": true,
      key: i
    })), children, Array.from({
      length: bleedElements
    }, (_, i) => /*#__PURE__*/React.createElement("li", {
      "aria-hidden": true,
      key: i
    }))];
  }
  return /*#__PURE__*/React.createElement(InternalProvider, null, /*#__PURE__*/React.createElement(CarouselContext.Provider, {
    value: {
      carouselContainer: targetRef,
      elementWidth,
      carouselWidthPx,
      variant,
      bleedElements,
      showControls,
      isHovering
    }
  }, /*#__PURE__*/React.createElement(CarouselWrapper, _extends({
    "data-aui": "carousel"
  }, props, {
    onMouseLeave: () => setIsHovering(false),
    onMouseEnter: () => setIsHovering(true)
  }), /*#__PURE__*/React.createElement(CarouselContainer, {
    as: as,
    tabIndex: 0,
    ref: targetRef,
    $elementWidth: elementWidth,
    $variant: variant,
    $bleedElements: bleedElements,
    $isPlaywright: window['isPlaywright']
  }, children), /*#__PURE__*/React.createElement(Footer, null, indicator, /*#__PURE__*/React.createElement(Controls, null)))));
}, {});